import React from 'react';
import { useTranslation } from 'react-i18next';
import { trackError } from 'utils/trackError';
import { getApiErrorMessage, parseApiError, translateKnownKey } from 'utils/apiError';
import { useChangePassword } from 'hooks/user';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from 'components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/dialog/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useAuth } from 'providers/AuthProvider';

export type ChangePasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const { mutateAsync: changePassword, isPending: isChangingPassword } = useChangePassword();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const schema = z
    .object({
      oldPassword: z.string().min(1, t('password_required')),
      newPassword: z.string().min(1, t('password_required')),
      confirmPassword: z.string().min(1, t('password_required')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('passwordsDoNotMatch'),
      path: ['confirmPassword'],
    });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const extractFieldErrors = (details: Record<string, unknown> | undefined): string => {
    if (!details) return '';
    const messages: string[] = [];
    for (const value of Object.values(details)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string') {
            messages.push(translateKnownKey(t, item) ?? t('errors.serverError'));
          }
        }
      }
    }
    return messages.join(' ');
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await changePassword({ oldPassword: data.oldPassword, newPassword: data.newPassword });
      form.reset();
      onOpenChange(false);
      toast.success(t('passwordChangedSuccessfully'));
      await logout();
      void navigate('/?loggedOut=true');
    } catch (err: unknown) {
      const { status, code, details } = parseApiError(err);
      const fieldMessage = extractFieldErrors(details);
      const message = fieldMessage || getApiErrorMessage(err, t);

      trackError(err, {
        context: 'changePassword',
        status,
        code,
      });

      form.setError('root', { message });
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(open: boolean) => {
        onOpenChange(open);
        if (!open) {
          form.reset();
          form.clearErrors();
        }
      }}
    >
      <DialogContent className="bg-white">
        <DialogHeader className="items-start">
          <DialogTitle>{t('changePassword')}</DialogTitle>
          <DialogDescription>{t('newPasswordDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="oldPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('currentPassword')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" autoComplete="current-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('newPassword')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" autoComplete="new-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('confirmNewPassword')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" autoComplete="new-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root?.message && (
              <div className="text-red-600 text-sm mb-2">{form.formState.errors.root.message}</div>
            )}

            <DialogFooter className="flex-row justify-end">
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                {t('cancel') || 'Cancel'}
              </Button>
              <Button type="submit" disabled={!form.formState.isValid || isChangingPassword}>
                {isChangingPassword ? t('saving') : t('changePassword')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
